import React, { useRef, useState, useEffect } from "react";
import { IHeaderParams } from "ag-grid-community";
import { Flex, Text, HelpIndicator, type TooltipPlacement } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { headerContainer, menuButton, sortIconContainer } from "./styles_vanilla.css";

type HeaderWithTooltipProps = Pick<
  IHeaderParams,
  "column" | "setSort" | "enableSorting" | "enableMenu" | "showColumnMenu"
> & {
  displayName: string;
  tooltipContent?: string;
  /**
   * Side the tooltip opens on. The overlay portals to the body and has no collision
   * detection, so `top` crops copy that is tall enough to clear the viewport edge on a
   * header row — pass `bottom` for those (COMP-4959).
   */
  tooltipPlacement?: TooltipPlacement;
};

export const HeaderWithTooltip = (props: HeaderWithTooltipProps) => {
  const { displayName, tooltipContent, tooltipPlacement = "top", column } = props;
  const { setSort, enableSorting } = props;
  const { enableMenu, showColumnMenu } = props;
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
  const menuButtonRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!enableSorting) return;
    const onSortChanged = () => {
      const currentSort = column.getSort();
      setSortDirection(currentSort as "asc" | "desc" | null);
    };
    column.addEventListener("sortChanged", onSortChanged);
    return () => {
      column.removeEventListener("sortChanged", onSortChanged);
    };
  }, [column, enableSorting]);

  const onSortClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!enableSorting) return;

    const nextSort = sortDirection === null ? "asc" : sortDirection === "asc" ? "desc" : null;
    setSort(nextSort, e.shiftKey);
    setSortDirection(nextSort);
  };

  return (
    <Flex
      className={headerContainer}
      alignItems="center"
      gap={8}
      onClick={onSortClicked}
      data-testid="header-container"
    >
      <Text variant="strong-md">{displayName}</Text>
      {tooltipContent && (
        <HelpIndicator
          id={`${displayName}-tooltip`}
          placement={tooltipPlacement}
          content={tooltipContent}
        >
          <Icon type="info-circle" variant="outline" />
        </HelpIndicator>
      )}
      {enableSorting && (
        <div className={sortIconContainer}>
          {sortDirection === "asc" && <Icon type="arrow-up" />}
          {sortDirection === "desc" && <Icon type="arrow-down" />}
        </div>
      )}
      {/* A custom header replaces AG Grid's own, so re-render its menu button here */}
      {enableMenu && !!column.getColDef().filter && (
        <span
          ref={menuButtonRef}
          role="presentation"
          data-testid="header-menu-button"
          className={`ag-header-cell-menu-button ${menuButton}`}
          // Without this the click bubbles to the header's sort handler
          onClick={(e) => {
            e.stopPropagation();
            if (menuButtonRef.current) showColumnMenu(menuButtonRef.current);
          }}
        >
          <span className="ag-icon ag-icon-menu" role="presentation" />
        </span>
      )}
    </Flex>
  );
};
